import { Button } from '@workspace/ui/components/button'
import { cn } from '@workspace/ui/lib/utils'
import { Check, FileSearch, MessageSquare, TrendingUp } from 'lucide-react'
import type { ReactNode } from 'react'
import { useEffect, useMemo, useState } from 'react'

const TOTAL_DURATION_SECONDS = 300

type PreparationStep = 'ANALYZING_DECK' | 'RESEARCHING' | 'PREPARING_QUESTIONS'

interface MilestoneConfig {
  icon: ReactNode
  label: string
  description: string
  /** The preparation step that corresponds to this milestone being active */
  step: PreparationStep
  /** Fallback: seconds after start when this milestone completes (if no step data) */
  completesAt: number
}

const MILESTONES: MilestoneConfig[] = [
  {
    icon: <FileSearch className="size-5" />,
    label: 'Analyzing Pitch Deck',
    description: 'Reviewing your presentation and extracting key information',
    step: 'ANALYZING_DECK',
    completesAt: TOTAL_DURATION_SECONDS * 0.1,
  },
  {
    icon: <TrendingUp className="size-5" />,
    label: 'Researching Market & Competition',
    description: 'Analyzing your market landscape and competitive positioning',
    step: 'RESEARCHING',
    completesAt: TOTAL_DURATION_SECONDS * 0.6,
  },
  {
    icon: <MessageSquare className="size-5" />,
    label: 'AI Investor Preparing Questions',
    description: 'Your AI investor is crafting tailored questions for your pitch',
    step: 'PREPARING_QUESTIONS',
    completesAt: TOTAL_DURATION_SECONDS * 1,
  },
]

/** Map a preparation step to the index of the currently active milestone. */
function stepToMilestoneIndex(step: PreparationStep | null | undefined): number | null {
  if (!step) return null
  return MILESTONES.findIndex((m) => m.step === step)
}

function formatRemaining(seconds: number): string {
  if (seconds <= 60) return 'less than a minute'
  const m = Math.ceil(seconds / 60)
  if (m === 1) return '1 minute'
  return `${m} minutes`
}

function getElapsedSeconds(createdAt: string | Date): number {
  const created = new Date(createdAt).getTime()
  return Math.max(0, Math.floor((Date.now() - created) / 1000))
}

interface PitchPreparationProgressProps {
  createdAt: string | Date
  preparationStep?: string | null
  onBack?: () => void
}

export function PitchPreparationProgress({
  createdAt,
  preparationStep,
  onBack,
}: PitchPreparationProgressProps) {
  const [elapsed, setElapsed] = useState(() => getElapsedSeconds(createdAt))

  useEffect(() => {
    const interval = setInterval(() => {
      setElapsed(getElapsedSeconds(createdAt))
    }, 1000)
    return () => clearInterval(interval)
  }, [createdAt])

  const remaining = Math.max(0, TOTAL_DURATION_SECONDS - elapsed)
  const progress = Math.min((elapsed / TOTAL_DURATION_SECONDS) * 100, 100)

  const stepIndex = stepToMilestoneIndex(preparationStep as PreparationStep)

  const activeMilestoneIndex = useMemo(() => {
    // Prefer the real preparation step from the backend
    if (stepIndex !== null && stepIndex >= 0) return stepIndex
    // Fallback to time-based estimation
    for (let i = MILESTONES.length - 1; i >= 0; i--) {
      const milestone = MILESTONES[i]
      if (milestone && elapsed >= milestone.completesAt) {
        return Math.min(i + 1, MILESTONES.length - 1)
      }
    }
    return 0
  }, [elapsed, stepIndex])

  return (
    <div className="w-full max-w-xl space-y-6">
      <div className="rounded-xl border border-border bg-input/50 p-6">
        {/* Header */}
        <div className="mb-6 text-center">
          <h2 className="text-lg font-semibold text-white">Preparing Your Pitch Session</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Hang tight while we set everything up
          </p>
        </div>

        {/* Progress bar */}
        <div className="mb-2">
          <div className="h-2 rounded-full bg-secondary">
            <div
              className="h-full rounded-full bg-[#f97316] transition-all duration-1000 ease-linear"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Countdown / Final message */}
        <p className="mb-6 text-center text-sm text-muted-foreground">
          {remaining > 0 ? (
            <>
              Should be ready in{' '}
              <span className="font-medium text-white">{formatRemaining(remaining)}</span>
            </>
          ) : (
            <span className="font-medium text-[#f97316] animate-pulse">
              Finishing up the last details — almost ready!
            </span>
          )}
        </p>

        {/* Milestones */}
        <div className="space-y-3">
          {MILESTONES.map((milestone, index) => {
            const isCompleted =
              stepIndex !== null ? index < stepIndex : elapsed >= milestone.completesAt
            const isActive = index === activeMilestoneIndex && !isCompleted

            return (
              <div
                key={milestone.label}
                className={cn(
                  'flex items-start gap-3 rounded-lg border p-3 transition-all duration-500',
                  isCompleted && 'border-emerald-500/30 bg-emerald-500/5',
                  isActive && 'border-[#f97316]/30 bg-[#f97316]/5',
                  !isCompleted && !isActive && 'border-border bg-transparent opacity-50'
                )}
              >
                <div
                  className={cn(
                    'mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full transition-colors duration-500',
                    isCompleted && 'bg-emerald-500/20 text-emerald-400',
                    isActive && 'bg-[#f97316]/20 text-[#f97316]',
                    !isCompleted && !isActive && 'bg-elevated text-muted-foreground'
                  )}
                >
                  {isCompleted ? <Check className="size-4" /> : milestone.icon}
                </div>
                <div className="min-w-0 flex-1">
                  <p
                    className={cn(
                      'text-sm font-medium transition-colors duration-500',
                      isCompleted && 'text-emerald-400',
                      isActive && 'text-white',
                      !isCompleted && !isActive && 'text-muted-foreground'
                    )}
                  >
                    {milestone.label}
                  </p>
                  <p className="text-xs text-muted-foreground">{milestone.description}</p>
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {onBack && (
        <Button variant="ghost" onClick={onBack} className="w-full">
          Go Back
        </Button>
      )}
    </div>
  )
}
